/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as React from 'react';
import { IGlobalLoaderViewProps } from './global-loader';

/**
 * Global Loader View
 * Event-based loader component that listens to DOM events
 * Can be added to any page via Site Builder
 */
const GlobalLoaderView: React.FC<IGlobalLoaderViewProps> = props => {
    const [isVisible, setIsVisible] = React.useState(false);
    const [message, setMessage] = React.useState<string | undefined>();
    const [showOverlay, setShowOverlay] = React.useState(true);
    const [className, setClassName] = React.useState<string>('');

    React.useEffect(() => {
        // Event listener for show event
        const handleShow = (event: CustomEvent) => {
            const { config = {}, message: msg } = event.detail || {};
            setIsVisible(true);
            setMessage(msg || config?.message);
            setShowOverlay(config?.showOverlay !== false);
            setClassName(config?.className || '');
        };

        // Event listener for hide event
        const handleHide = () => {
            setIsVisible(false);
            setMessage(undefined);
        };

        // Event listener for update event
        const handleUpdate = (event: CustomEvent) => {
            const { message: msg } = event.detail;
            setMessage(msg);
        };

        // Add event listeners
        window.addEventListener('globalloader:show', handleShow as EventListener);
        window.addEventListener('globalloader:hide', handleHide as EventListener);
        window.addEventListener('globalloader:update', handleUpdate as EventListener);

        // Cleanup
        return () => {
            window.removeEventListener('globalloader:show', handleShow as EventListener);
            window.removeEventListener('globalloader:hide', handleHide as EventListener);
            window.removeEventListener('globalloader:update', handleUpdate as EventListener);
        };
    }, []);

    console.log('GlobalLoader available');

    // Don't render if not visible
    if (!isVisible) {
        return null;
    }

    return (
        <div className='global-loader-wrapper'>
            {/* Overlay backdrop */}
            {showOverlay && (
                <div className='global-loader__overlay' role='presentation' aria-hidden='true' data-testid='global-loader-overlay' />
            )}

            {/* Loader container */}
            <div
                className={`global-loader ${className}`.trim()}
                role='alert'
                aria-live='assertive'
                aria-busy='true'
                data-testid='global-loader'
            >
                <div className='global-loader__content'>
                    {/* Spinner */}
                    <div className='global-loader__spinner' aria-hidden='true'>
                        <div className='global-loader__spinner-circle' />
                    </div>

                    {/* Optional message */}
                    {message && (
                        <div className='global-loader__message' aria-label={message}>
                            {message}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GlobalLoaderView;
